import { IconWrapper } from '@flexo/general';
import { ALERT, alertCallbackStore, METADATA, RIGHT_DRAWER } from '@flexo/reducers';
import {
  CircularProgress,
  TableCell,
  TableFooter,
  TableSortLabel,
} from '@mui/material';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Collapsible } from './helpers';
import { useStickyTableFooterEntry } from './sticky-table-footer';
import uStyles from './admin-users-table.module.scss';
import styles from './table.module.scss';
import { adminUserMatchesSearch } from '../admin-users-search.utils';

const ENV = (import.meta as any).env;
const EMPTY_COMMUNITIES: any[] = [];

const getMetadataCommunities = (metadata: any) =>
  [
    metadata?.communities,
    metadata?.entities?.community,
    metadata?.entities?.communities,
    metadata?.sortedEntities?.communities,
  ].find((communities) => Array.isArray(communities) && communities.length > 0) ||
  EMPTY_COMMUNITIES;

/**
 * AdminUsersActiveTable
 * Infinite-scroll table for active admin users.
 * Loads batches of 50 automatically as the user scrolls down.
 */
export function AdminUsersActiveTable() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);

  const [loadingDisable, setLoadingDisable] = useState<Record<string, boolean>>(
    {}
  );
  const [isExpanded, setIsExpanded] = useState(true);
  const expandedBeforeSearch = useRef<boolean | null>(null);
  const tableRef = useRef<HTMLDivElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  const placeholderRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);
  const titlePlaceholderRef = useRef<HTMLDivElement>(null);
  const isExpandedRef = useRef(isExpanded);
  isExpandedRef.current = isExpanded;

  // JS-based sticky title + header via rAF polling
  // Runs once for the lifetime of the component — reads isExpanded via ref
  useEffect(() => {
    let titleStuck = false;
    let headerStuck = false;
    let headerHeight = 0;
    let titleHeight = 0;
    let rafId: number | null = null;
    let scrollContainer: HTMLElement | null = null;
    let fixedHeaderBottom = 0;

    const findScrollContainer = (el: HTMLElement | null): HTMLElement | null => {
      if (!el) return null;

      const ionContent = el.closest('ion-content') as any;
      if (ionContent?.shadowRoot) {
        const innerScroll = ionContent.shadowRoot.querySelector('.inner-scroll');
        if (innerScroll) return innerScroll as HTMLElement;
      }

      let parent: HTMLElement | null = el.parentElement;
      while (parent) {
        const overflowY = window.getComputedStyle(parent).overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') {
          return parent;
        }
        parent = parent.parentElement;
      }

      return null;
    };

    const measureFixedHeaderBottom = (anchor: HTMLElement): number => {
      let maxBottom = 0;

      const check = (el: Element) => {
        const computed = window.getComputedStyle(el as HTMLElement);
        if (computed.position !== 'fixed') return;

        const rect = (el as HTMLElement).getBoundingClientRect();
        if (rect.top <= 2 && rect.height > 0 && rect.bottom > maxBottom) {
          maxBottom = rect.bottom;
        }
      };

      let node: HTMLElement | null = anchor;
      while (node) {
        let sibling = node.previousElementSibling;
        while (sibling) {
          check(sibling);
          sibling.querySelectorAll('*').forEach(check);
          sibling = sibling.previousElementSibling;
        }
        node = node.parentElement;
      }

      return maxBottom;
    };

    const refreshFixedHeader = () => {
      if (!titleRef.current) return;
      scrollContainer =
        scrollContainer || findScrollContainer(titleRef.current);
      fixedHeaderBottom = measureFixedHeaderBottom(titleRef.current);
    };

    const unstickTitle = () => {
      const title = titleRef.current;
      const titlePlaceholder = titlePlaceholderRef.current;
      if (title) {
        title.style.position = '';
        title.style.top = '';
        title.style.left = '';
        title.style.width = '';
        title.style.zIndex = '';
      }
      if (titlePlaceholder) {
        titlePlaceholder.style.height = '0px';
      }
      titleStuck = false;
    };

    const unstickHeader = () => {
      const header = headerRef.current;
      const placeholder = placeholderRef.current;
      if (header) {
        header.style.position = '';
        header.style.top = '';
        header.style.width = '';
        header.style.left = '';
        header.style.zIndex = '';
      }
      if (placeholder) {
        placeholder.style.height = '0px';
      }
      headerStuck = false;
    };

    const update = () => {
      if (!isExpandedRef.current) {
        if (titleStuck) unstickTitle();
        if (headerStuck) unstickHeader();
        return;
      }

      if (!scrollContainer && titleRef.current) {
        scrollContainer = findScrollContainer(titleRef.current);
      }
      if (!fixedHeaderBottom && titleRef.current) {
        fixedHeaderBottom = measureFixedHeaderBottom(titleRef.current);
      }

      const baseline = Math.max(
        scrollContainer?.getBoundingClientRect().top || 0,
        fixedHeaderBottom
      );

      // Sticky collapse title (always active)
      const titlePh = titlePlaceholderRef.current;
      const title = titleRef.current;
      const tableRect = tableRef.current?.getBoundingClientRect();
      const titlePhRect = titlePh?.getBoundingClientRect();
      if (
        !tableRect ||
        tableRect.width === 0 ||
        tableRect.height === 0 ||
        !titlePhRect ||
        titlePhRect.width === 0
      ) {
        if (titleStuck) unstickTitle();
        if (headerStuck) unstickHeader();
        return;
      }
      if (!titlePh || !title) {
        titleStuck = false;
        titleHeight = 0;
      }
      if (titlePh && title) {
        const titleRect = titlePh.getBoundingClientRect();
        if (titleRect.top <= baseline && !titleStuck) {
          titleHeight = title.offsetHeight;
          titleStuck = true;
          titlePh.style.height = `${titleHeight}px`;
          title.style.position = 'fixed';
          title.style.top = `${baseline}px`;
          title.style.left = `${titleRect.left}px`;
          title.style.width = `${titleRect.width}px`;
          title.style.zIndex = '11';
        } else if (titleRect.top > baseline && titleStuck) {
          titleStuck = false;
          titlePh.style.height = '0px';
          title.style.position = '';
          title.style.top = '';
          title.style.left = '';
          title.style.width = '';
          title.style.zIndex = '';
        } else if (titleStuck) {
          title.style.left = `${titleRect.left}px`;
          title.style.width = `${titleRect.width}px`;
          title.style.top = `${baseline}px`;
        }
      }

      const placeholder = placeholderRef.current;
      const header = headerRef.current;
      if (!placeholder || !header) {
        headerStuck = false;
        headerHeight = 0;
        return;
      }

      const rect = placeholder.getBoundingClientRect();
      const headerThreshold = baseline + (titleStuck ? titleHeight : 0);

      if (rect.top <= headerThreshold && !headerStuck) {
        headerHeight = header.offsetHeight;
        headerStuck = true;
        placeholder.style.height = `${headerHeight}px`;
        header.style.position = 'fixed';
        header.style.top = `${headerThreshold}px`;
        header.style.width = `${rect.width}px`;
        header.style.left = `${rect.left}px`;
        header.style.zIndex = '10';
      } else if (rect.top > headerThreshold && headerStuck) {
        headerStuck = false;
        placeholder.style.height = '0px';
        header.style.position = '';
        header.style.top = '';
        header.style.width = '';
        header.style.left = '';
        header.style.zIndex = '';
      } else if (headerStuck) {
        header.style.left = `${rect.left}px`;
        header.style.width = `${rect.width}px`;
        header.style.top = `${headerThreshold}px`;
      }
    };

    refreshFixedHeader();
    window.addEventListener('resize', refreshFixedHeader);
    const refreshTimer = window.setInterval(refreshFixedHeader, 1000);

    const poll = () => {
      update();
      rafId = requestAnimationFrame(poll);
    };
    rafId = requestAnimationFrame(poll);

    return () => {
      if (rafId) cancelAnimationFrame(rafId);
      window.removeEventListener('resize', refreshFixedHeader);
      window.clearInterval(refreshTimer);
      unstickTitle();
      unstickHeader();
    };
  }, []);

  useEffect(() => {
    if (isExpanded) return;

    if (titlePlaceholderRef.current) {
      titlePlaceholderRef.current.style.height = '0px';
    }
    if (titleRef.current) {
      titleRef.current.style.position = '';
      titleRef.current.style.top = '';
      titleRef.current.style.left = '';
      titleRef.current.style.width = '';
      titleRef.current.style.zIndex = '';
    }
    if (placeholderRef.current) {
      placeholderRef.current.style.height = '0px';
    }
    if (headerRef.current) {
      headerRef.current.style.position = '';
      headerRef.current.style.top = '';
      headerRef.current.style.width = '';
      headerRef.current.style.left = '';
      headerRef.current.style.zIndex = '';
    }
  }, [isExpanded]);

  const getUserId = (row: any) =>
    row._raw?.userId || row.userId || row._raw?.clerkUserId || row.clerkUserId;

  // Titles and headers from widgets.tables.*
  const { title, rows } = t('widgets.tables.AdminUsersActiveTable', {
    returnObjects: true,
  }) as any;
  const getTitleByField = (field: string, fallback: string) =>
    rows?.find((r: any) => r.field === field)?.title || fallback;
  const getSortableByField = (field: string, fallback = true) =>
    (rows?.find((r: any) => r.field === field)?.sortable ??
      fallback) as boolean;
  const thUsers = getTitleByField('user', 'Users');
  const thEmail = getTitleByField('email', 'Email');
  const thActivated = getTitleByField('activated', 'Activated');
  const thRole = getTitleByField('role', 'Role');
  const thCommunities = getTitleByField('communities', 'Communities');
  const thActions = getTitleByField('actions', 'Actions');
  const columns = [
    { title: thUsers, field: 'user', sortable: getSortableByField('user') },
    { title: thEmail, field: 'email', sortable: getSortableByField('email') },
    {
      title: thActivated,
      field: 'activated',
      sortable: getSortableByField('activated'),
    },
    { title: thRole, field: 'role', sortable: getSortableByField('role') },
    {
      title: thCommunities,
      field: 'communities',
      sortable: getSortableByField('communities'),
    },
    { title: thActions, field: 'actions', sortable: false },
  ] as const;

  const allActiveUsers = useSelector((state: any) => state.metadata?.adminsActive) || [];
  const pagination = useSelector((state: any) => state.metadata?.adminsActivePagination);
  const loadingStatus = useSelector((state: any) => state.metadata?.AdminsActiveAsyncStatus);
  const adminSearchText = useSelector((state: any) => state.metadata?.adminSearchText) || '';
  const allCommunities = useSelector((state: any) =>
    getMetadataCommunities(state.metadata)
  );
  const organizationId = user?.organizationId || ENV.VITE_APP_ORG_ID;

  // Client-side sort state
  const [localSortBy, setLocalSortBy] = useState<string>('user');
  const [localSortDir, setLocalSortDir] = useState<'asc' | 'desc'>('asc');

  const activeUsers = useMemo(() => {
    let filtered = allActiveUsers;
    if (adminSearchText) {
      filtered = allActiveUsers.filter((a: any) =>
        adminUserMatchesSearch(a, adminSearchText, allCommunities)
      );
    }
    const sorted = [...filtered];
    sorted.sort((a: any, b: any) => {
      const valA = (a?.[localSortBy] || '').toString().toLowerCase();
      const valB = (b?.[localSortBy] || '').toString().toLowerCase();
      if (valA < valB) return localSortDir === 'asc' ? -1 : 1;
      if (valA > valB) return localSortDir === 'asc' ? 1 : -1;
      return 0;
    });
    return sorted;
  }, [allActiveUsers, adminSearchText, allCommunities, localSortBy, localSortDir]);

  const activeUsersStickyFooter = useMemo(
    () => () => (
      <TableContainer
        component={Paper}
        className={`${uStyles.AdminUsersTable} ${uStyles.AdminUsersTableActive} ${uStyles.AdminUsersStickyFooter}`}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
        }}
      >
        <Table aria-label="admin active users sticky footer">
          <TableFooter>
            <TableRow>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__users}`}
              >
                <div className={styles.Table__Footer__Cell}>
                  <span className={uStyles.AdminUsers__FooterCount}>
                    {t('adminUsers.hints.countUsers', {
                      count: activeUsers.length,
                      defaultValue: `${activeUsers.length} Users`,
                    })}
                  </span>
                </div>
              </TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__email}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__activated}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__role}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__communities}`}
              ></TableCell>
              <TableCell
                className={`${styles.Table__Cell__Static} ${uStyles.AdminUsers__Col__actions}`}
              ></TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableContainer>
    ),
    [activeUsers.length, t]
  );

  const hasStickyFooterHost = useStickyTableFooterEntry(
    useMemo(
      () =>
        activeUsers.length > 0
          ? {
              id: 'admin-users-active',
              enabled: isExpanded,
              sectionRef: tableRef,
              focusRef: titlePlaceholderRef,
              viewportRef: tableRef,
              contentRef: tableRef,
              renderFooter: activeUsersStickyFooter,
            }
          : null,
      [activeUsers.length, isExpanded, activeUsersStickyFooter]
    )
  );

  // Auto-expand when search finds results, restore on clear
  useEffect(() => {
    if (adminSearchText) {
      if (expandedBeforeSearch.current === null) {
        expandedBeforeSearch.current = isExpanded;
      }
      if (activeUsers.length > 0) {
        setIsExpanded(true);
      }
    } else if (expandedBeforeSearch.current !== null) {
      setIsExpanded(expandedBeforeSearch.current);
      expandedBeforeSearch.current = null;
    }
  }, [adminSearchText, activeUsers.length]);

  const currentPage = pagination?.page || 1;
  const currentPageSize = pagination?.pageSize || 50;
  const totalItems = pagination?.totalItems || 0;
  const totalPages = pagination?.totalPages || 1;
  const currentSortBy = pagination?.sortBy || 'user';
  const currentSortDir = pagination?.sortDir || 'asc';
  const currentSearch = pagination?.search || '';
  const hasMore = currentPage < totalPages;
  const isLoadingMore = loadingStatus === 'LOADING_MORE';
  const isStillFetching = hasMore || isLoadingMore || loadingStatus === 'PENDING';

  // Auto-fetch next page as soon as current one completes
  useEffect(() => {
    if (loadingStatus === 'SUCCESS' && hasMore) {
      dispatch(
        METADATA.SetAdminsPaginatedAsync({
          dispatch,
          organizationId,
          status: 'active',
          page: currentPage + 1,
          pageSize: currentPageSize,
          search: currentSearch || undefined,
          sortBy: currentSortBy,
          sortDir: currentSortDir,
          isLoadMore: true,
        }) as any
      );
    }
  }, [loadingStatus, hasMore, currentPage, currentPageSize, currentSearch, currentSortBy, currentSortDir, organizationId]);

  const dispatchFresh = useCallback(
    (overrides: Record<string, any> = {}) => {
      dispatch(
        METADATA.SetAdminsPaginatedAsync({
          dispatch,
          organizationId,
          status: 'active',
          page: 1,
          pageSize: currentPageSize,
          search: currentSearch || undefined,
          sortBy: currentSortBy,
          sortDir: currentSortDir,
          isRefresh: true,
          isLoadMore: false,
          ...overrides,
        }) as any
      );
    },
    [dispatch, organizationId, currentPageSize, currentSearch, currentSortBy, currentSortDir]
  );

  function handleRowClick(row: any) {
    dispatch(
      RIGHT_DRAWER.SetDrawer({ type: 'ADMIN_UPDATE_USER', data: row } as any)
    );
  }

  function handleSort(field: string) {
    if (isStillFetching) return;
    if (localSortBy === field) {
      setLocalSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setLocalSortBy(field);
      setLocalSortDir('asc');
    }
  }

  function handleDisableUser(row: any, e: any) {
    e.stopPropagation();
    e.preventDefault();

    const userId = getUserId(row);

    if (!userId) {
      console.error('Cannot disable user: no userId available');
      return;
    }

    alertCallbackStore.setData({
      userId,
      organizationId,
    });

    dispatch(
      ALERT.SetAlert({
        value: {
          type: 'ALERT_REMOVE_USER',
        },
      } as any)
    );
  }

  const allInvitedUsers = useSelector((state: any) => state.metadata?.adminsInvited) || [];
  const invitedFiltered = useMemo(() => {
    if (!adminSearchText) return allInvitedUsers;
    return allInvitedUsers.filter((a: any) =>
      adminUserMatchesSearch(a, adminSearchText, allCommunities)
    );
  }, [allInvitedUsers, adminSearchText, allCommunities]);

  const bothEmpty = activeUsers.length === 0 && invitedFiltered.length === 0 && !!adminSearchText;

  // Don't render while loading initial data
  if (loadingStatus === 'PENDING') {
    return null;
  }

  if (activeUsers.length === 0 && !isLoadingMore && !adminSearchText) {
    return <></>;
  }

  if (bothEmpty) {
    return (
      <div className={`${styles.Table__EmptyState} mt-5`}>
        {t('widgets.search.no_results')}
      </div>
    );
  }

  return (
    <div
      ref={tableRef}
      className={`${styles.Table} ${uStyles.AdminUsersTable} ${uStyles.AdminUsersTableActive} ${
        !isExpanded ? uStyles.AdminUsersTableCollapsed : ''
      }`}
      style={{ display: activeUsers.length === 0 ? 'none' : undefined }}
    >
      <div ref={titlePlaceholderRef} />
      <div
        ref={titleRef}
        className={`${styles.Table__Title} ${uStyles.AdminUsers__Title} ${uStyles.AdminUsers__CollapseHeader} ${isExpanded ? uStyles.AdminUsers__CollapseHeader__Expanded : uStyles.AdminUsers__CollapseHeader__Collapsed}`}
        onClick={() => setIsExpanded((prev) => !prev)}
      >
        <span>{title}</span>
        <IconWrapper
          iconName="chevron-down"
          className={uStyles.AdminUsers__CollapseChevron}
        />
      </div>
      {isExpanded && (
        <div
          ref={placeholderRef}
          className={uStyles.AdminUsers__StickyHeaderPlaceholder}
        />
      )}
      {isExpanded && (
        <div
          ref={headerRef}
          className={uStyles.AdminUsers__StickyHeaderWrapper}
        >
          <TableContainer
            component={Paper}
            className={uStyles.AdminUsers__StickyHeader}
          >
            <Table aria-label="admin active users header">
              <TableHead>
                <TableRow>
                  {columns.map((column: any, idx: number) => (
                    <TableCell
                      key={`active_head_${column.field}_${idx}`}
                      className={`
                      ${
                        column.sortable
                          ? styles.Table__Header__Cell__Sortable
                          : styles.Table__Header__Cell__Static
                      }
                      ${
                        column.sortable && localSortBy === column.field
                          ? styles.Table__Header__Cell__Active
                          : styles.Table__Header__Cell
                      }
                      ${uStyles.AdminUsers__TH}
                      ${
                        (uStyles as any)[
                          'AdminUsers__Col__' + String(column.field)
                        ] || ''
                      }
                    `}
                    >
                      {column.sortable ? (
                        <TableSortLabel
                          active={localSortBy === column.field}
                          direction={
                            localSortBy === column.field &&
                            localSortDir === 'asc'
                              ? 'asc'
                              : 'desc'
                          }
                          onClick={() => handleSort(column.field)}
                          className={`${styles.Table__Header__Cell}`}
                          style={isStillFetching ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
                          IconComponent={() =>
                            localSortBy === column.field ? (
                              localSortDir === 'asc' ? (
                                <IconWrapper
                                  iconName="chevron-up"
                                  className={`${styles.Table__TH__Icon}`}
                                />
                              ) : (
                                <IconWrapper
                                  iconName="chevron-down"
                                  className={`${styles.Table__TH__Icon}`}
                                />
                              )
                            ) : (
                              <div style={{ height: 32, width: 19 }}></div>
                            )
                          }
                        >
                          <div>{(column.title as string).toUpperCase()}</div>
                        </TableSortLabel>
                      ) : (
                        <div>{(column.title as string).toUpperCase()}</div>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
            </Table>
          </TableContainer>
        </div>
      )}
      <Collapsible
        open={isExpanded}
        className={uStyles.AdminUsers__Collapsible}
      >
        <div className={uStyles.AdminUsers__TableWrapper}>
          {/* Rows */}
          {activeUsers.map((row: any, rowIndex: number) => (
            <TableContainer
              component={Paper}
              key={`active_row_${getUserId(row) || rowIndex}`}
              className={uStyles.AdminUsers__RowContainer}
            >
              <Table aria-label={`admin active users row ${rowIndex}`}>
                <TableBody>
                  <TableRow
                    className={uStyles.AdminUsers__Row}
                    onClick={() => !isStillFetching && handleRowClick(row)}
                    sx={{
                      '&:last-child td, &:last-child th': { border: 0 },
                      opacity: isStillFetching ? 0.6 : 1,
                      pointerEvents: isStillFetching ? 'none' : 'auto',
                    }}
                  >
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__users}`}
                    >
                      {row.user}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__email}`}
                    >
                      {row.email}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__activated}`}
                    >
                      {row.activated}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__role}`}
                    >
                      {row.role}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__communities}`}
                    >
                      {row.communities}
                    </TableCell>
                    <TableCell
                      className={`${styles.Table__Cell__Static} ${uStyles.AdminUsersTables__Body__Row__Cell} ${uStyles.AdminUsers__Col__actions}`}
                    >
                      <div
                        className="flex flex-align-center"
                        style={{
                          gap: 8,
                          cursor: (isStillFetching || loadingDisable[getUserId(row)])
                            ? 'not-allowed'
                            : 'pointer',
                        }}
                        onClick={(e: any) => {
                          if (!isStillFetching && !loadingDisable[getUserId(row)]) {
                            handleDisableUser(row, e);
                          }
                        }}
                      >
                        <IconWrapper
                          className={uStyles.AdminUsers__Icon}
                          iconName="delete_kwick"
                        />
                      </div>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          ))}
        </div>

        {!hasStickyFooterHost ? (
          <div className={uStyles.AdminUsers__Count}>
            {t('adminUsers.hints.countUsers', {
              count: activeUsers.length,
              defaultValue: `${activeUsers.length} Users`,
            })}
          </div>
        ) : null}
      </Collapsible>
    </div>
  );
}

export default AdminUsersActiveTable;
